/** What the user sees when a route cannot render.
 *
 * Until the pre-go-live audit there was nothing here at all: no `errorElement`
 * on either route tree and no catch-all, so React Router fell back to its own
 * development error page. Two things reached it, and both are ordinary:
 *
 * 1. **A stale tab asking for a chunk that no longer exists.** Every screen in
 *    this app is a `lazy()` import, and `infra/nginx/default.conf` answers a
 *    missing hashed asset with 404 — deliberately, because the alternative was
 *    index.html wearing a `.js` name. But a 404 makes the dynamic import reject,
 *    the rejection escapes `<Suspense>` (which catches pending, never failed),
 *    and the whole tree unmounts. The nginx comment said the client "reloads and
 *    picks up the current index.html"; nothing in the app actually did that.
 *    This is the piece that makes that sentence true.
 *
 * 2. **A URL that matches no route.** A stale bookmark or a typo produced
 *    "Unexpected Application Error!" rather than a 404.
 *
 * The two are told apart because the remedies differ. A missing chunk is fixed
 * by reloading — the user did nothing wrong and there is nothing to decide, so
 * the reload is offered as the primary action and the page says why. A 404 is
 * fixed by going somewhere real, and reloading it forever would not help.
 *
 * Deliberately styled with the design system rather than a bare `<div>`: this
 * screen appears at the worst moment, and one that looks like a crash reads as
 * data loss even when nothing was lost.
 *
 * The one piece of logic here — telling a stale chunk apart from a real bug —
 * lives in `lib/chunk-error.ts` and is unit-tested there. The frontend suite
 * runs with `environment: 'node'` and no DOM, so what is left in this file is
 * markup that the production build and the route-tree test cover instead.
 */
import { FileQuestion, RefreshCw, TriangleAlert } from 'lucide-react'
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom'

import { isChunkLoadError } from '../lib/chunk-error'
import { Button } from './ui/button'
import { EmptyState } from './ui/empty-state'

export function RouteError() {
  const error = useRouteError()
  const navigate = useNavigate()

  // A 404 from the router itself: no route matched. `isRouteErrorResponse`
  // covers the catch-all below and any loader that threw a Response.
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />
  }

  if (isChunkLoadError(error)) {
    return (
      <EmptyState
        icon={RefreshCw}
        tone="warning"
        title="This page was updated while you had it open"
        description="The version of the app in this tab is no longer available. Reloading picks up the current one — nothing you were working on has been sent anywhere."
        action={
          <Button onClick={() => window.location.reload()}>
            <RefreshCw aria-hidden />
            Reload the page
          </Button>
        }
      />
    )
  }

  // Anything else. The message is not shown: it is a stack in disguise, and
  // `lib/api.ts` already refuses to put internal shapes on screen.
  return (
    <EmptyState
      icon={TriangleAlert}
      tone="critical"
      title="Something went wrong on this screen"
      description="The page could not be displayed. Reloading usually clears it; if it keeps happening, tell the service desk what you were doing."
      action={
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button onClick={() => window.location.reload()}>
            <RefreshCw aria-hidden />
            Reload
          </Button>
          <Button variant="outline" onClick={() => void navigate(-1)}>
            Go back
          </Button>
        </div>
      }
    />
  )
}

/** The catch-all. Rendered for a URL that matches no route in either tree. */
export function NotFound() {
  const navigate = useNavigate()
  return (
    <EmptyState
      icon={FileQuestion}
      title="Page not found"
      description="This address does not match anything in the service desk. It may have moved, or the link may be out of date."
      action={
        <Button variant="outline" onClick={() => void navigate(-1)}>
          Go back
        </Button>
      }
    />
  )
}
